import React, { memo } from "react";
import Media from "@qlik/qlik/src/components/Media";
import LanguageSwitcher from "@qlik/qlik/src/components/Footer/LanguageSwitcher";
import FooterCommunity from "@qlik/qlik/src/components/Footer/FooterCommunity";
import FooterBottomMenu from "@qlik/qlik/src/components/Footer/FooterBottomMenu";
import FooterMainMenu from "@qlik/qlik/src/components/Footer/FooterMainMenu";
import useSiteMetadata from "@qlik/qlik/src/hooks/useSiteMetaData";
import QlikLogoWhite from "@qlik/qlik/src/assets/images/qlik-logo-white.inline.svg";
import * as styles from "./styles.module.css";

/**
 * Footer component
 * This component renders the footer of the page.
 * @param {Object} data - The data to be used in the footer.
 */
const Footer = ({ data, className, footerType = "Default" }) => {
    const { footerContent, socialMenu, privacyMenu, copyrightText, verticalMenu } = data || {};
    const { media } = footerContent || {};
    const { title: socialMenuTitle, menuItems: socialLinks } = socialMenu || {};
    const { menuItems: bottomLinks } = privacyMenu || {};
    const { title: defaultTitle } = useSiteMetadata();

    // Footer and sub-element classes and naming based on footer type
    const footerStyleConfig = {
        Default: {
            wrapperClassName: "footerTypeDefault",
            wrapperClasses: "py-15 lg:pt-[51px] lg:pb-20 bg-gradient-to-b from-stone-light to-white-750",
            subFooterClassName: "subFooterDefault",
            subFooterClasses: "flex flex-col-reverse lg:flex-row pt-7.5 lg:justify-between",
            border: "border-t border-stone/50",
        },
        "Bottom section only": {
            wrapperClassName: "footerTypeBottomSectionOnly",
            subFooterClasses: "flex flex-col-reverse lg:flex-row pt-7.5 lg:justify-between",
            subFooterClassName: "subFooterBottomSectionOnly",
        },
        Minimal: {
            wrapperClassName: "footerTypeMinimal",
            wrapperClasses: "py-5 bg-gray text-white",
            subFooterClassName: "subFooterMinimal",
            subFooterClasses: "flex flex-col xl:flex-row xl:justify-between",
        },
    };

    // Define variable for footer classes based on footer type, leveraging the footerStyleConfig object
    const footerClasses = footerStyleConfig[footerType] || footerStyleConfig["Default"];

    // The main footer navigation component
    const FooterNav = () => {
        return (
            <div className="col-12 pb-14.75 lg:pb-7">
                <FooterMainMenu verticalMenu={verticalMenu} />
            </div>
        );
    };

    // Logo, social links, and community section
    const FooterLogoCommunitySocial = () => {
        return (
            <>
                <div className="col-12">
                    <div
                        className={`flex flex-col lg:flex-row lg:items-center lg:justify-between pt-15.25 ${footerClasses?.border ??
                            ""}`}
                    >
                        {media && (
                            <Media
                                className="h-7.5 max-w-full object-contain object-left-top"
                                media={media?.[0]}
                                alt={defaultTitle}
                            />
                        )}
                        <FooterCommunity
                            title={socialMenuTitle}
                            className="flex max-lg:pt-7.5 max-lg:justify-between lg:items-center lg:gap-7.5"
                            socialLinks={socialLinks}
                        />
                    </div>
                </div>
            </>
        );
    };

    // Very bottom portion of the footer. Minimal version offers most variation
    const SubFooter = () => {
        return (
            <div className="col-12">
                <div
                    className={`${styles.subFooter} ${footerClasses.subFooterClasses} ${
                        styles[footerClasses.subFooterClassName]
                    }`}
                >
                    <div>
                        {footerType === "Minimal" && <QlikLogoWhite />}
                        <FooterBottomMenu bottomLinks={bottomLinks} />
                        <div>
                            <p className={`text-xs opacity-70 ${styles.copyrightText}`}>{copyrightText}</p>
                        </div>
                    </div>
                    {footerType !== "Minimal" && (
                        // Render the language switcher for all footer types except Minimal
                        <LanguageSwitcher className="lg:basis-50 flex-shrink-0" />
                    )}
                </div>
            </div>
        );
    };

    // Putting it all together
    return (
        <footer
            className={`${className} paragraph-tracking-normal 
            ${footerClasses.wrapperClasses ?? footerStyleConfig["Default"].wrapperClasses}
            ${styles[footerClasses.wrapperClassName]}`}
        >
            <div className="container">
                {footerType === "Default" && (
                    <div className="row">
                        <FooterNav />
                    </div>
                )}
                <div className="row">
                    {footerType !== "Minimal" && <FooterLogoCommunitySocial />}
                    <SubFooter />
                </div>
            </div>
        </footer>
    );
};

// Export the memoized version of the Footer component
export default memo(Footer);
